import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Star, MessageSquare, ClipboardCheck, Phone } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const waLink = (phone, link) => {
  const p = (phone || "").replace(/[^0-9]/g, "");
  const num = p.length === 10 ? "91" + p : p;
  const msg = encodeURIComponent(
    `Thank you for choosing Veloskin! We'd love your feedback — please leave us a quick Google review: ${link}`
  );
  return `https://wa.me/${num}?text=${msg}`;
};
const smsLink = (phone, link) =>
  `sms:${phone}?&body=${encodeURIComponent(`Please leave us a Google review: ${link}`)}`;

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="border-brand-line shadow-none" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="h-4 w-4" />
        <span className="text-xs">{label}</span>
      </div>
      <p className="mt-1 font-display text-2xl font-semibold text-brand-ink">{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </CardContent>
  </Card>
);

export const CrmTasks = () => {
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);

  const load = useCallback(() => {
    api.get("/crm/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/crm/review-jobs").then(({ data }) => setJobs(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const markSent = async (j) => {
    try {
      await api.patch(`/jobs/${j.job_id}/google-review`, { sent: true });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const toggleDone = async (j, v) => {
    try {
      await api.patch(`/jobs/${j.job_id}/google-review`, { completed: v });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!summary) return null;
  const s = summary;
  return (
    <div className="mb-8" data-testid="crm-tasks">
      <h2 className="mb-3 font-display text-lg font-semibold text-brand-ink">CRM tracked tasks — {s.month}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={ShieldCheck}
          label="Timely warranties"
          value={`${s.warranties.timely}/${s.warranties.registered}`}
          sub="within 7 days of delivery"
          testid="crm-stat-warranties"
        />
        <Stat
          icon={ClipboardCheck}
          label="Protection registrations"
          value={s.protection.registered}
          sub={`${s.protection.pending} pending`}
          testid="crm-stat-protection"
        />
        <Stat
          icon={MessageSquare}
          label="Feedback calls taken"
          value={`${s.feedback.done}/${s.feedback.delivered_jobs}`}
          sub="this month's deliveries"
          testid="crm-stat-feedback"
        />
        <Stat
          icon={Star}
          label="Google reviews"
          value={s.google_reviews.confirmed}
          sub={`${s.google_reviews.sent} requests sent`}
          testid="crm-stat-reviews"
        />
      </div>

      <Card className="mt-4 border-brand-line shadow-none">
        <CardContent className="p-4">
          <p className="mb-3 text-sm font-semibold text-brand-ink">
            Google review requests <span className="font-normal text-slate-500">· jobs with feedback completed</span>
          </p>
          {jobs.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="crm-review-empty">No feedback-completed jobs yet.</p>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => (
                <div
                  key={j.job_id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-brand-line p-3"
                  data-testid={`crm-review-row-${j.job_id}`}
                >
                  <div className="min-w-[160px]">
                    <p className="text-sm font-medium text-brand-ink">{j.customer_name}</p>
                    <p className="text-xs text-slate-500">
                      Delivered {j.delivery_date} · {j.phone || "no phone"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <a href={waLink(j.phone, j.review_link)} target="_blank" rel="noreferrer" onClick={() => markSent(j)}>
                      <Button size="sm" variant="outline" data-testid={`crm-review-wa-${j.job_id}`}>
                        <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                      </Button>
                    </a>
                    <a href={smsLink(j.phone, j.review_link)} onClick={() => markSent(j)}>
                      <Button size="sm" variant="outline" data-testid={`crm-review-sms-${j.job_id}`}>
                        <Phone className="mr-1.5 h-3.5 w-3.5" /> SMS
                      </Button>
                    </a>
                    {j.review_sent && <span className="text-xs text-slate-500">sent</span>}
                    <label className="flex items-center gap-1.5 text-xs text-brand-ink">
                      <Checkbox
                        checked={j.review_completed}
                        onCheckedChange={(v) => toggleDone(j, !!v)}
                        data-testid={`crm-review-done-${j.job_id}`}
                      />
                      Review completed
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
